import { t } from 'i18next';
import { StarIcon } from 'lucide-react';
import { useState } from 'react';

import { useBuilderStateContext } from '../builder-hooks';

import { useChatStore } from './useChatStore';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { AiBuilderReviewApi } from '@/lib/ai-builder-review-api';

const MAX_RATE = 5;

type AddReviewDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment: string) => void;
};

const StarRating = ({
  rating,
  hoverRating,
  onRatingChange,
  onHoverChange,
}: {
  rating: number;
  hoverRating: number;
  onRatingChange: (value: number) => void;
  onHoverChange: (value: number) => void;
}) => {
  return (
    <div className="flex gap-1 items-center mt-2 justify-center">
      {Array.from({ length: MAX_RATE }).map((_, index) => (
        <button
          key={index}
          type="button"
          onMouseEnter={() => onHoverChange(index + 1)}
          onMouseLeave={() => onHoverChange(0)}
          onClick={() => onRatingChange(index + 1)}
          className="focus:outline-none"
        >
          <StarIcon
            className={`w-8 h-8 ${
              (hoverRating || rating) > index
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

export const AddReviewDialog = ({
  open,
  onClose,
  onSubmit,
}: AddReviewDialogProps) => {
  const [comment, setComment] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [flowVersion, flow] = useBuilderStateContext((state) => [
    state.flowVersion,
    state.flow,
  ]);
  const { chats } = useChatStore();
  const messages = chats[flow.id] || [];
  const handleSubmit = () => {
    onSubmit(rating, comment);
    AiBuilderReviewApi.send({
      flow,
      history: messages.map((message) => ({
        content: message.content,
        role: message.userType === 'user' ? 'user' : 'assistant',
      })),
      flowVersion,
      rating,
      comment,
    });
    setComment('');
    setRating(0);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('Review')}</DialogTitle>
          <DialogDescription>
            {t('Review the quality of the AI messages/generation')}
          </DialogDescription>
        </DialogHeader>

        <StarRating
          rating={rating}
          hoverRating={hoverRating}
          onRatingChange={setRating}
          onHoverChange={setHoverRating}
        />

        <div className="mt-3">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('Comment')}
            className="w-full resize-none"
            rows={3}
          />
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={rating === 0}>
            {t('Send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

AddReviewDialog.displayName = 'AddReviewDialog';
